/**
 * ConnectorInstallModalV2 — V2 redesign of the marketplace install flow.
 *
 * Replaces v1's `?legacy=1&install=<manifestId>` deep-link punt. Opens
 * directly in the V2 page, calls createConnector() with manifest defaults,
 * then hands the new installed instance to the parent (which typically
 * opens the Configure modal immediately so the operator can paste creds).
 *
 * Props:
 *   - isOpen            : boolean
 *   - manifest          : marketplace manifest (with name, vendor, authType,
 *                          baseUrl, configSchema, skills, etc.)
 *   - tenantId, orgId   : passed through to createConnector
 *   - onClose           : () => void
 *   - onInstalled       : (createdConnector) => void  (parent decides next step)
 */
import React, { useState } from 'react';
// Phase 5B — Modal → Slideout per G378. The install flow is a
// preview + confirm + install (~0 form fields here, but it chains
// into the connector config slideout right after, so keeping both
// surfaces on the same right-rail pattern preserves a smooth
// install → configure flow without a modal stack-up.
import { Slideout, Button, Pill } from './index';
import { CheckCircle2, AlertCircle, Plug } from 'lucide-react';
import { createConnector } from '../../services/api';

export function ConnectorInstallModalV2({
  isOpen,
  manifest,
  tenantId,
  orgId,
  onClose,
  onInstalled,
}) {
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !manifest) return null;

  const skillCount = Array.isArray(manifest.skills) ? manifest.skills.length : 0;
  const guardrailCount = Array.isArray(manifest.guardrails) ? manifest.guardrails.length : 0;
  const requiredFieldCount = Array.isArray(manifest.configSchema)
    ? manifest.configSchema.filter((f) => f.required).length
    : (Array.isArray(manifest.authFields) ? manifest.authFields.length : 0);

  const handleInstall = async () => {
    setInstalling(true);
    setError(null);
    try {
      /* G166 — Set `type` to the manifest id (not the category). The backend
       * test/health dispatchers route by type, so per-manifest beans like
       * GcpSecretManagerPlatformConnector can only be selected if the stored
       * type uniquely identifies the manifest. v1 stored the category here,
       * which lumped every secrets-vault entry under "secrets-vault" and
       * forced the dispatcher into a generic HTTP probe (the source of the
       * earlier "HTTP 404 from https://secretmanager.googleapis.com" error). */
      const payload = {
        name: manifest.name || manifest.displayName,
        description: manifest.description,
        type: manifest.id || manifest.manifestId || manifest.category || manifest.marketplaceCategory,
        url: manifest.baseUrl || '',
        authMethod: manifest.authType || 'none',
        authHeader: manifest.authField || '',
        tenantId,
        orgId,
      };
      const result = await createConnector(payload);
      if (!result || result.ok === false) {
        setError(result?.error || result?.data?.error || `Install failed (HTTP ${result?.status || '?'})`);
        setInstalling(false);
        return;
      }
      /* Hand the new instance up — parent typically opens Configure next. */
      onInstalled && onInstalled(result.data);
    } catch (e) {
      setError(e?.message || 'Network error installing connector');
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={installing ? undefined : onClose}
      width={560}
      ariaLabel="Install connector"
      header={
        <div className="flex items-center gap-2">
          <Plug size={16} className="text-v2-cyan" aria-hidden="true" />
          <h2 className="text-[15px] font-bold m-0">Install connector</h2>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} disabled={installing}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleInstall}
            loading={installing}
            iconLeft={<CheckCircle2 size={13} />}
          >
            {installing ? 'Installing…' : 'Install'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-v2-md flex items-center justify-center font-bold text-[14px] bg-v2-cyan-soft text-v2-cyan flex-shrink-0">
            {(manifest.icon && manifest.icon.startsWith('http')) ? (
              <img src={manifest.icon} alt="" className="w-6 h-6" />
            ) : '🔌'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-v2-ink m-0">{manifest.name || manifest.displayName}</div>
            <div className="text-[12px] text-v2-ink-secondary mt-0.5">
              {manifest.vendor ? `${manifest.vendor} · ` : ''}{manifest.version ? `v${manifest.version}` : ''}
              {manifest.tier ? ` · ${manifest.tier}` : ''}
            </div>
          </div>
        </div>

        {manifest.description && (
          <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed m-0">
            {manifest.description}
          </p>
        )}

        <div className="flex flex-wrap gap-1.5">
          {manifest.authType && <Pill tone="trust">Auth: {manifest.authType}</Pill>}
          {skillCount > 0 && <Pill tone="muted">{skillCount} skills</Pill>}
          {guardrailCount > 0 && <Pill tone="muted">{guardrailCount} guardrails</Pill>}
          {manifest.category && <Pill tone="cyan">{manifest.category}</Pill>}
        </div>

        <div className="rounded-v2-sm border border-v2-border-subtle bg-v2-muted/40 p-3 space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-v2-ink-muted">After install you will</div>
          <ol className="list-decimal list-inside text-[12px] text-v2-ink-secondary space-y-1 m-0 pl-0">
            <li>Configure {requiredFieldCount > 0 ? `${requiredFieldCount} required ${requiredFieldCount === 1 ? 'field' : 'fields'}` : 'authentication credentials'} (e.g. service-account JSON, project, OAuth client).</li>
            <li>Run a Test Connection to verify the credentials reach the vendor.</li>
            <li>Bind the connector's skills to agents that need them.</li>
          </ol>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-v2-sm border border-v2-danger-border bg-v2-danger-soft p-3">
            <AlertCircle size={14} className="text-v2-danger flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-[12px] text-v2-danger leading-relaxed">{error}</div>
          </div>
        )}
      </div>
    </Slideout>
  );
}

export default ConnectorInstallModalV2;
